import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { jwtDecode } from "jwt-decode"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const shortenAddressWithCenterDots = (
  addr: string,
  subNumStart = 4,
  subNumEnd = 4
) => {
  if (!addr?.length) return ""
  const length = addr.length
  if (length <= subNumStart + subNumEnd) return addr
  return `${addr.substring(0, 2 + subNumStart)}...${addr.substring(
    length - subNumEnd
  )}`
}

export const BASE_SIGN_MESSAGE =
  "Hi there from Avacus Gas Sponsor! Please sign this message to prove that you can access our service. For security reasons, do not sign this message outside Avacus Gas Sponsor, and here's a unique message ID they can't guess: "

export const isTokenExpired = (token: string) => {
  if (!token) return true
  try {
    const decodedToken = jwtDecode(token)
    const currentTime = Date.now() / 1000

    if (!decodedToken.exp) return true

    return decodedToken.exp < currentTime
  } catch {
    return true
  }
}

export const isTokenMatchingAddress = (
  token: string,
  address: string
): boolean => {
  if (!token || !address) return false
  try {
    const decoded = jwtDecode<{ wallet_address?: string; address?: string }>(
      token
    )
    const tokenAddress = decoded.wallet_address || decoded.address
    if (!tokenAddress) return false
    return tokenAddress.toLowerCase() === address.toLowerCase()
  } catch {
    return false
  }
}

/**
 * Convert IPFS URL to use a reliable gateway
 * @param url - The URL to convert
 * @returns Converted URL using ipfs.wakumo.net gateway
 */
export const convertIpfsUrl = (url: string): string => {
  if (!url) return ""
  if (url.startsWith("https://ipfs.io"))
    return url.replace("https://ipfs.io", "https://ipfs.wakumo.net")
  if (url.startsWith("ipfs://ipfs/"))
    return url.replace("ipfs://ipfs/", "https://ipfs.wakumo.net/ipfs/")
  if (url.startsWith("ipfs://"))
    return url.replace("ipfs://", "https://ipfs.wakumo.net/ipfs/")
  return url
}

export interface FormatCurrencyOptions {
  locale?: string
  currency?: string
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

/**
 * Format number as currency using Intl.NumberFormat.
 * Use for all USD (or other currency) display.
 */
export function formatCurrency(
  amount: number | string | undefined | null,
  options: FormatCurrencyOptions = {}
): string {
  const num = amount != null ? Number(amount) : 0
  if (Number.isNaN(num)) return "0.00"
  const {
    locale = "en-US",
    currency = "USD",
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num)
}

/**
 * Format number for display (no currency symbol). Use for token amounts etc.
 */
export function formatNumber(
  amount: number | string | undefined | null,
  options: Intl.NumberFormatOptions = {}
): string {
  const num = amount != null ? Number(amount) : 0
  if (Number.isNaN(num)) return "0"
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
    ...options,
  }).format(num)
}

/**
 * Clamp a decimal string to a maximum number of fraction digits.
 * Preserves integer part and trims trailing dot/zeros via optional normalizer.
 */
export function clampDecimalString(
  value: string,
  maxFractionDigits: number,
  options?: {
    normalize?: (value: string) => string
  }
): string {
  if (!value || maxFractionDigits < 0) return value

  const [integerPart, decimalPart] = value.split(".")

  if (decimalPart == null) {
    return value
  }

  const truncatedValue = `${integerPart}.${decimalPart.slice(
    0,
    maxFractionDigits
  )}`

  return options?.normalize ? options.normalize(truncatedValue) : truncatedValue
}

export function countGraphemes(str: string): number {
  // Check if Intl.Segmenter is available (newer browsers)
  if (
    typeof Intl !== "undefined" &&
    "Segmenter" in Intl &&
    typeof (Intl as Record<string, unknown>).Segmenter === "function"
  ) {
    try {
      const Segmenter = (Intl as Record<string, unknown>).Segmenter as new (
        locale: string,
        options: { granularity: "grapheme" }
      ) => {
        segment: (str: string) => Iterable<{ segment: string }>
      }
      const segmenter = new Segmenter("en", { granularity: "grapheme" })
      return Array.from(segmenter.segment(str)).length
    } catch {
      // Fallback if Segmenter fails
      return str.length
    }
  }
  // Fallback for browsers without Intl.Segmenter
  return str.length
}

/**
 * Truncate a string to a maximum number of graphemes
 * Preserves grapheme boundaries (e.g., emojis, combined characters)
 * @param str - The string to truncate
 * @param maxLength - Maximum number of graphemes
 * @returns Truncated string
 */
export function truncateGraphemes(str: string, maxLength: number): string {
  if (!str || maxLength <= 0) return ""

  // Check if Intl.Segmenter is available (newer browsers)
  if (
    typeof Intl !== "undefined" &&
    "Segmenter" in Intl &&
    typeof (Intl as Record<string, unknown>).Segmenter === "function"
  ) {
    try {
      const Segmenter = (Intl as Record<string, unknown>).Segmenter as new (
        locale: string,
        options: { granularity: "grapheme" }
      ) => {
        segment: (str: string) => Iterable<{ segment: string }>
      }
      const segmenter = new Segmenter("en", { granularity: "grapheme" })
      const segments = Array.from(segmenter.segment(str))
      if (segments.length <= maxLength) return str
      return segments
        .slice(0, maxLength)
        .map((s) => s.segment)
        .join("")
    } catch {
      // Fallback if Segmenter fails
      return str.slice(0, maxLength)
    }
  }
  // Fallback for browsers without Intl.Segmenter
  return str.slice(0, maxLength)
}

/**
 * Smart wrap long words by inserting zero-width spaces
 * This allows CSS to break long words at these points
 * @param text - The text to wrap
 * @param longWordThreshold - Words longer than this will be wrapped (default: 20)
 * @param chunkSize - Size of chunks to break long words into (default: 4)
 * @returns The text with zero-width spaces inserted in long words
 */
export function smartWrap(
  text: string,
  longWordThreshold = 20,
  chunkSize = 4
): string {
  if (!text) return ""
  return text
    .split(/(\s+)/)
    .map((token) => {
      if (token.trim().length <= longWordThreshold) return token
      return token.replace(new RegExp(`.{1,${chunkSize}}`, "g"), "$&\u200B")
    })
    .join("")
}

/**
 * Creates a promise that resolves after the specified number of milliseconds
 * @param ms - The number of milliseconds to delay
 * @returns A promise that resolves after the delay
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}
